/**
 * QuestionDetailDrawer
 *
 * P2 — Right-slide panel surfacing per-question explainability:
 *   • Selection rationale (priority score breakdown from the backend's
 *     calculate_question_priority helper)
 *   • Why excluded, when applicable
 *   • Knowledge-base sources cited (when the question was auto-drafted)
 *   • The current refined / drafted response in full
 *   • The improvement summary from the rubric scorer
 *
 * The drawer reads everything from the QuestionMatrixRow it's given;
 * no fetching, no fabrication. Closing is via overlay click or Esc.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { X, FileText, Database, BarChart3, AlertTriangle, Sparkles, Award, ShieldCheck, ThumbsUp, ThumbsDown } from 'lucide-react';
import {
  buildRetrievalSnapshots,
  postTenderBankFeedback,
  FeedbackAction,
} from '../services/tenderBankFeedbackApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { theme } from '../styles/theme';
import { QuestionMatrixRow, RefinedResponse, PastTenderMatch } from '../types';
import ResponseComparisonCard from './ResponseComparisonCard';

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, ${({ $open }) => ($open ? '0.45' : '0')});
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: background 0.2s ease;
  z-index: 1100;
`;

const Panel = styled.aside<{ $open: boolean; $wide: boolean; $inline: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $inline, $open, $wide }) =>
    $inline
      ? `
        position: static;
        width: auto;
        height: auto;
        background: transparent;
        box-shadow: none;
        transform: none;
      `
      : `
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        /* Wider when we render the full Refine card; narrower for explainability-only. */
        width: ${$wide ? 'min(960px, 96vw)' : 'min(560px, 92vw)'};
        background: ${theme.colors.surface};
        box-shadow: ${theme.shadows.xl};
        transform: translateX(${$open ? '0' : '100%'});
        transition: transform 0.25s ease, width 0.25s ease;
        z-index: 1200;
      `}
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.border};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  flex-shrink: 0;
  gap: ${theme.spacing[4]};
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  h3 {
    margin: 0 0 ${theme.spacing[1]};
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.medium};
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  p {
    margin: 0;
    font-size: ${theme.typography.fontSize.lg};
    line-height: ${theme.typography.lineHeight.tight};
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.base};
  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }
`;

const Body = styled.div<{ $inline?: boolean }>`
  padding: ${({ $inline }) => ($inline ? `0` : theme.spacing[5])};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
  ${({ $inline }) =>
    $inline
      ? `
        overflow: visible;
      `
      : `
        overflow-y: auto;
        flex: 1;
      `}
`;

const Section = styled.section``;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin: 0 0 ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: ${theme.spacing[2]} ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  span:first-child {
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.medium};
  }
`;

const ScoreBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ScoreBar = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 60px;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  .label {
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.medium};
  }
  .track {
    height: 8px;
    background: ${theme.colors.backgroundSecondary};
    border-radius: ${theme.borderRadius.full};
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.primary[600]});
  }
  .value {
    text-align: right;
    color: ${theme.colors.textPrimary};
    font-variant-numeric: tabular-nums;
  }
`;

const ResponseBox = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  max-height: 360px;
  overflow-y: auto;

  p { margin: 0 0 ${theme.spacing[2]} 0; }
  p:last-child { margin-bottom: 0; }
  ul, ol { margin: 0 0 ${theme.spacing[2]} ${theme.spacing[4]}; padding: 0; }
  strong { font-weight: ${theme.typography.fontWeight.semibold}; color: ${theme.colors.textPrimary}; }
`;

const StatusPill = styled.span<{ $tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${({ $tone }) => {
    switch ($tone) {
      case 'success':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'warning':
        return `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`;
      case 'error':
        return `background:${theme.colors.error[100]};color:${theme.colors.error[700]};`;
      case 'info':
        return `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`;
      default:
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`;
    }
  }}
`;

const SourceItem = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};

  .title {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.medium};
  }
  .meta {
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const Helper = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

/* ================================================================
 * INLINE WORKSPACE — design refresh for mode='inline' + wide rows
 *
 * When the drawer renders inline for a refined/drafted row (i.e., a
 * row with a real AI draft to review), we adopt a manuscript-review
 * layout: editorial header → AI draft hero → audit trail accordions.
 * Primary work surface lives at the top; audit metadata is one click
 * away. Drawer mode and non-wide inline keep the original layout.
 * ================================================================ */

const Workspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const WorkspaceHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const QEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  font-size: 11px;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: ${theme.typography.fontWeight.semibold};

  & > span.sep {
    opacity: 0.4;
    letter-spacing: 0;
  }
`;

const QEyebrowChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily.mono};
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: none;
`;

const QTitle = styled.h2`
  margin: 0;
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  line-height: ${theme.typography.lineHeight.tight};
  color: ${theme.colors.textPrimary};
  letter-spacing: ${theme.typography.letterSpacing.tight};
  max-width: 78ch;
`;

const StatStrip = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, max-content));
  gap: ${theme.spacing[3]} ${theme.spacing[6]};
  margin: 0;
  padding: ${theme.spacing[4]} 0 0;
  border-top: 1px solid ${theme.colors.borderLight};
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  & dt {
    margin: 0;
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  & dd {
    margin: 0;
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    font-variant-numeric: tabular-nums;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeroFrame = styled.div`
  position: relative;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};

  /* A 2px navy accent stripe along the top — quiet but signals 'this is the focal element'. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${theme.colors.primary[500]};
    border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
  }
`;

const HeroInner = styled.div`
  padding: ${theme.spacing[5]} ${theme.spacing[6]} ${theme.spacing[6]};
`;

const AuditRule = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border};
  }

  & > span {
    font-family: ${theme.typography.fontFamily.display};
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.22em;
    white-space: nowrap;
  }
`;

const AccordionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const Accordion = styled.details`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  transition: border-color ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  overflow: hidden;

  &:hover {
    border-color: ${theme.colors.borderHover};
  }

  &[open] {
    border-color: ${theme.colors.primary[200]};
    box-shadow: ${theme.shadows.sm};
  }

  & > summary {
    list-style: none;
    cursor: pointer;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    user-select: none;
    transition: background ${theme.transitions.base};
  }

  & > summary:hover {
    background: ${theme.colors.surfaceHover};
  }

  & > summary::-webkit-details-marker { display: none; }

  /* Custom chevron — small caret rendered with borders, rotates when open. */
  & > summary::after {
    content: '';
    display: inline-block;
    margin-left: auto;
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-right: 1.5px solid ${theme.colors.textMuted};
    border-bottom: 1.5px solid ${theme.colors.textMuted};
    transform: rotate(-45deg);
    transition: transform 0.2s ease;
  }

  &[open] > summary::after {
    transform: rotate(45deg);
  }

  & > summary > .icon {
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
  }

  &[open] > summary > .icon {
    color: ${theme.colors.primary[500]};
  }
`;

const AccordionTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AccordionTrailingBadge = styled.span<{ $tone?: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: ${theme.borderRadius.full};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.02em;
  text-transform: none;
  ${({ $tone = 'neutral' }) => {
    switch ($tone) {
      case 'success':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'warning':
        return `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`;
      case 'error':
        return `background:${theme.colors.error[100]};color:${theme.colors.error[700]};`;
      case 'info':
        return `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`;
      default:
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textSecondary};`;
    }
  }}
`;

const AccordionBody = styled.div`
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.borderLight};
  background: ${theme.colors.backgroundSecondary};
  animation: accordion-reveal 0.2s ease-out;

  /* Inside the accordion, the existing SectionTitle is redundant with our
   * summary line — hide it so we don't get double headings. */
  & h4 {
    display: none;
  }

  @keyframes accordion-reveal {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const PPNNote = styled.p`
  margin: 0;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-left: 2px solid ${theme.colors.primary[300]};
  border-radius: 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-style: italic;
`;

interface Props {
  row: QuestionMatrixRow | null;
  onClose: () => void;
  /** Optional: AI model id used during refinement / drafting. PPN-compliance disclosure. */
  modelId?: string;
  /** Wired through to ResponseComparisonCard for the Refine workflow. */
  onHumanRefine?: (
    questionId: string,
    prompt: string,
    callbacks: {
      onChunk: (chunk: string) => void;
      onComplete: (finalResponse: string) => void;
      onError: (error: Error) => void;
    }
  ) => void;
  onAcceptRefinement?: (questionId: string, refinedText: string) => void;
  /** Phase 6 — needed to post counterfactual-learning feedback events. */
  companyId?: string;
  /** Render mode. 'drawer' = right-slide panel with overlay + header (default).
   *  'inline' = body sections only, for embedding inside an expanded matrix row. */
  mode?: 'drawer' | 'inline';
}

const STATUS_LABEL: Record<string, { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }> = {
  refined: { label: 'Refined', tone: 'success' },
  drafted: { label: 'Drafted by AI', tone: 'info' },
  needs_draft: { label: 'Awaiting draft', tone: 'warning' },
  user_excluded: { label: 'Excluded', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'success' },
};

const SELECTION_STATUS_LABEL: Record<string, { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }> = {
  selected: { label: 'Selected for refinement', tone: 'success' },
  below_threshold: { label: 'Below selection threshold', tone: 'warning' },
  capped_out: { label: 'Above threshold but past max-questions cap', tone: 'warning' },
  disqualified: { label: 'Disqualified by content pattern', tone: 'error' },
  no_response: { label: 'No inline bidder response in source', tone: 'info' },
};

const QuestionDetailDrawer: React.FC<Props> = ({
  row,
  onClose,
  modelId,
  onHumanRefine,
  onAcceptRefinement,
  companyId,
  mode = 'drawer',
}) => {
  const open = !!row;
  const inline = mode === 'inline';

  // Phase 6 — feedback state. Per-chunk action so the UI can disable
  // the buttons after a click and confirm visually. Keyed by chunk_id.
  const [feedbackActions, setFeedbackActions] = useState<Record<string, FeedbackAction>>({});

  const submitFeedback = useCallback(async (
    targetMatch: PastTenderMatch,
    action: FeedbackAction,
  ) => {
    if (!row || !companyId || !row.past_matches) return;
    const chunkKey = `${targetMatch.tender_id}#${targetMatch.chunk_index ?? 0}`;
    if (feedbackActions[chunkKey]) return; // single-shot per match per session

    setFeedbackActions((prev) => ({ ...prev, [chunkKey]: action }));

    const targetIndex = row.past_matches.findIndex(
      (m) => m.tender_id === targetMatch.tender_id && m.chunk_index === targetMatch.chunk_index,
    );

    await postTenderBankFeedback({
      question: row.question,
      company_id: companyId,
      bid_context: null,  // populated server-side from state when available
      config_snapshot: targetMatch.boost_breakdown
        ? { from_breakdown: true }
        : null,
      retrieved: buildRetrievalSnapshots(row.past_matches),
      action,
      target_chunk_id: chunkKey,
      target_rank: targetIndex >= 0 ? targetIndex + 1 : null,
    });
  }, [row, companyId, feedbackActions]);

  // Esc to close — only meaningful in drawer mode (inline rows have no overlay).
  useEffect(() => {
    if (!open || inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, inline]);

  // Scroll-to-top on open — only relevant when the Body is the scroll container
  // (drawer mode). Inline mode lets the page scroll naturally.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open || inline) return;
    // Double rAF: the first frame queues the work, the second runs after
    // ResponseComparisonCard's heavy children (markdown, version history,
    // diff view) have laid out — so the reset survives their reflow.
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [open, row?.question_id, inline]);

  const meta = row?.selection_metadata;
  const breakdown = meta?.breakdown || {};
  const statusInfo = row ? STATUS_LABEL[row.status] : null;
  const selectionInfo = meta ? SELECTION_STATUS_LABEL[meta.status] : null;

  // Find the highest weight scoring component for the score-bar denominator.
  const weights = {
    type: 30,
    length: 25,
    criteria: 30,
    keywords: 15,
  };

  // Refined / drafted rows render the full ResponseComparisonCard inside a
  // wider drawer. needs_draft / user_excluded rows fall back to the
  // explainability-only narrow view.
  const wide = row?.status === 'refined' || row?.status === 'drafted';

  // Adapt a QuestionMatrixRow to the RefinedResponse shape that
  // ResponseComparisonCard expects.
  const adaptedRefined: RefinedResponse | null = row
    ? {
        question_id: row.question_id,
        question: row.question,
        original_response: row.original_response || '',
        refined_response: row.current_response || '',
        improvement_score:
          typeof row.improvement_score === 'number' ? row.improvement_score : null,
        improvement_summary: row.improvement_summary || '',
      }
    : null;

  // In inline mode there's no slide animation, so a null row should render
  // nothing rather than an empty container.
  if (inline && !row) return null;

  return (
    <>
      {!inline && <Overlay $open={open} onClick={onClose} aria-hidden={!open} />}
      <Panel
        $open={open}
        $wide={wide}
        $inline={inline}
        role={inline ? undefined : 'dialog'}
        aria-modal={inline ? undefined : 'true'}
        aria-hidden={inline ? undefined : !open}
      >
        {row && (
          <>
            {!inline && (
              <Header>
                <HeaderText>
                  <h3>
                    {row.question_id}
                    {row.section ? ` · ${row.section}` : ''}
                    {row.type ? ` · ${row.type}` : ''}
                  </h3>
                  <p>{row.question}</p>
                </HeaderText>
                <CloseButton onClick={onClose} aria-label="Close">
                  <X size={18} />
                </CloseButton>
              </Header>
            )}

            <Body ref={bodyRef} $inline={inline}>
              {inline && wide && row ? (
                <Workspace>
                  <WorkspaceHeader>
                    <QEyebrow>
                      <QEyebrowChip>{row.question_id}</QEyebrowChip>
                      {row.section && (
                        <>
                          <span className="sep">/</span>
                          <span>{row.section}</span>
                        </>
                      )}
                      {row.type && (
                        <>
                          <span className="sep">·</span>
                          <span>{row.type}</span>
                        </>
                      )}
                    </QEyebrow>
                    <QTitle>{row.question}</QTitle>
                    <StatStrip>
                      <Stat>
                        <dt>Status</dt>
                        <dd>
                          {statusInfo ? (
                            <StatusPill $tone={statusInfo.tone}>{statusInfo.label}</StatusPill>
                          ) : (
                            row.status
                          )}
                        </dd>
                      </Stat>
                      <Stat>
                        <dt>Criterion</dt>
                        <dd>{row.criterion_label || '—'}</dd>
                      </Stat>
                      {row.suggested_words ? (
                        <Stat>
                          <dt>Target</dt>
                          <dd>~{row.suggested_words} words</dd>
                        </Stat>
                      ) : null}
                      <Stat>
                        <dt>Score</dt>
                        <dd>
                          {typeof row.improvement_score === 'number'
                            ? `${row.improvement_score}%`
                            : 'Pending'}
                        </dd>
                      </Stat>
                      {modelId && (
                        <Stat>
                          <dt>Model</dt>
                          <dd>{modelId}</dd>
                        </Stat>
                      )}
                    </StatStrip>
                  </WorkspaceHeader>

                  {/* Hero — the AI draft + Accept/Reject/Regenerate/AI Refine.
                      This is what the user clicked View to see. It renders
                      first so it's never below the fold. */}
                  {adaptedRefined && (
                    <HeroFrame>
                      <HeroInner>
                        <ResponseComparisonCard
                          refinedResponse={adaptedRefined}
                          index={0}
                          questionType={row.type}
                          criterionLabel={row.criterion_label}
                          onHumanRefine={onHumanRefine}
                          onAcceptRefinement={onAcceptRefinement}
                          isUngrounded={row.kb_retrieval?.coverage?.assessment === 'ungrounded'}
                        />
                      </HeroInner>
                    </HeroFrame>
                  )}

                  <AuditRule>
                    <span>Audit trail</span>
                  </AuditRule>

                  <AccordionStack>
                    {/* Why selected — score breakdown */}
                    {meta && (
                      <Accordion>
                        <summary>
                          <span className="icon"><BarChart3 size={14} /></span>
                          <AccordionTitle>
                            Why this question {meta.status === 'selected' ? 'was selected' : meta.status === 'no_response' ? 'is awaiting a draft' : 'was not refined'}
                          </AccordionTitle>
                          {selectionInfo && (
                            <AccordionTrailingBadge $tone={selectionInfo.tone}>
                              {selectionInfo.label}
                            </AccordionTrailingBadge>
                          )}
                        </summary>
                        <AccordionBody>
                          <MetaRow style={{ marginBottom: theme.spacing[3] }}>
                            <span>Final score</span>
                            <span>
                              {typeof meta.final_score === 'number'
                                ? `${meta.final_score.toFixed(1)} / 100`
                                : '—'}
                            </span>
                            <span>Threshold</span>
                            <span>{meta.threshold}</span>
                          </MetaRow>
                          {(typeof breakdown.type === 'number' ||
                            typeof breakdown.length === 'number' ||
                            typeof breakdown.criteria === 'number' ||
                            typeof breakdown.keywords === 'number') && (
                            <ScoreBars>
                              {(['type', 'length', 'criteria', 'keywords'] as const).map((k) => {
                                const v = (breakdown as any)[k] as number | undefined;
                                if (typeof v !== 'number') return null;
                                const w = weights[k];
                                const pct = Math.max(0, Math.min(100, (v / w) * 100));
                                return (
                                  <ScoreBar key={k}>
                                    <span className="label">
                                      {k === 'type' && 'Question type'}
                                      {k === 'length' && 'Response length'}
                                      {k === 'criteria' && 'Criterion match'}
                                      {k === 'keywords' && 'Keywords'}
                                    </span>
                                    <span className="track">
                                      <span className="fill" style={{ width: `${pct}%`, display: 'block' }} />
                                    </span>
                                    <span className="value">{v.toFixed(1)} / {w}</span>
                                  </ScoreBar>
                                );
                              })}
                            </ScoreBars>
                          )}
                        </AccordionBody>
                      </Accordion>
                    )}

                    {/* What changed in this draft / refinement */}
                    {row.improvement_summary && (
                      <Accordion>
                        <summary>
                          <span className="icon"><Sparkles size={14} /></span>
                          <AccordionTitle>
                            What changed in this {row.status === 'drafted' ? 'draft' : 'refinement'}
                          </AccordionTitle>
                        </summary>
                        <AccordionBody>
                          <Helper style={{ color: theme.colors.textPrimary }}>
                            {row.improvement_summary}
                          </Helper>
                        </AccordionBody>
                      </Accordion>
                    )}

                    {/* Similar past responses — compact summary list */}
                    {row.past_matches && row.past_matches.length > 0 && (
                      <Accordion>
                        <summary>
                          <span className="icon"><Database size={14} /></span>
                          <AccordionTitle>Similar past responses from your tender bank</AccordionTitle>
                          <AccordionTrailingBadge $tone="info">
                            {row.past_matches.length} {row.past_matches.length === 1 ? 'match' : 'matches'}
                          </AccordionTrailingBadge>
                        </summary>
                        <AccordionBody>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                            {row.past_matches.slice(0, 5).map((m, i) => (
                              <div
                                key={`${m.tender_id}-${i}`}
                                style={{
                                  background: theme.colors.surface,
                                  border: `1px solid ${theme.colors.border}`,
                                  borderRadius: theme.borderRadius.md,
                                  padding: theme.spacing[3],
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.semibold,
                                    color: theme.colors.textPrimary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={m.tender_title}
                                >
                                  {m.tender_title}
                                </div>
                                <div style={{
                                  display: 'flex',
                                  gap: theme.spacing[2],
                                  fontSize: theme.typography.fontSize.xs,
                                  color: theme.colors.textMuted,
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                }}>
                                  {typeof m.relevance_score === 'number' && (
                                    <span>
                                      Relevance{' '}
                                      <strong style={{ color: theme.colors.textPrimary }}>
                                        {(m.relevance_score * 100).toFixed(0)}%
                                      </strong>
                                    </span>
                                  )}
                                  {m.outcome && (
                                    <StatusPill
                                      $tone={m.outcome === 'won' ? 'success' : m.outcome === 'lost' ? 'error' : 'neutral'}
                                    >
                                      {m.outcome}
                                    </StatusPill>
                                  )}
                                  {m.match_type === 'qa' && (
                                    <StatusPill $tone="info">Q&amp;A match</StatusPill>
                                  )}
                                </div>
                              </div>
                            ))}
                            {row.past_matches.length > 5 && (
                              <Helper>
                                + {row.past_matches.length - 5} more matches not shown.
                              </Helper>
                            )}
                          </div>
                        </AccordionBody>
                      </Accordion>
                    )}

                    {/* KB coverage — compact summary */}
                    {row.source === 'drafted' && row.kb_retrieval && (
                      <Accordion>
                        <summary>
                          <span className="icon"><ShieldCheck size={14} /></span>
                          <AccordionTitle>Knowledge base coverage</AccordionTitle>
                          {row.kb_retrieval.coverage?.assessment && (
                            <AccordionTrailingBadge
                              $tone={
                                ['excellent', 'good'].includes((row.kb_retrieval.coverage.assessment || '').toLowerCase())
                                  ? 'success'
                                  : (row.kb_retrieval.coverage.assessment || '').toLowerCase() === 'fair'
                                    ? 'info'
                                    : (row.kb_retrieval.coverage.assessment || '').toLowerCase() === 'poor'
                                      ? 'warning'
                                      : 'error'
                              }
                            >
                              {row.kb_retrieval.coverage.assessment}
                            </AccordionTrailingBadge>
                          )}
                        </summary>
                        <AccordionBody>
                          <MetaRow>
                            {typeof row.kb_retrieval.coverage?.confidence_score === 'number' && (
                              <>
                                <span>Confidence</span>
                                <span>{(row.kb_retrieval.coverage.confidence_score * 100).toFixed(0)}%</span>
                              </>
                            )}
                            {typeof row.kb_retrieval.source_count === 'number' && (
                              <>
                                <span>Sources retrieved</span>
                                <span>{row.kb_retrieval.source_count}</span>
                              </>
                            )}
                            {typeof row.kb_retrieval.avg_relevance === 'number' && (
                              <>
                                <span>Avg. relevance</span>
                                <span>{(row.kb_retrieval.avg_relevance * 100).toFixed(0)}%</span>
                              </>
                            )}
                          </MetaRow>
                        </AccordionBody>
                      </Accordion>
                    )}
                  </AccordionStack>

                  <PPNNote>
                    Disclosure of AI use is required by the UK Cabinet Office Procurement Policy
                    Note (PPN) on AI in bids. The status, model, and per-question scoring are the
                    audit trail for this question.
                  </PPNNote>
                </Workspace>
              ) : (
              <>
              <Section>
                <SectionTitle>
                  <FileText size={14} /> Status &amp; criterion
                </SectionTitle>
                <MetaRow>
                  <span>Status</span>
                  <span>
                    {statusInfo ? (
                      <StatusPill $tone={statusInfo.tone}>{statusInfo.label}</StatusPill>
                    ) : (
                      row.status
                    )}
                  </span>
                  <span>Criterion</span>
                  <span>{row.criterion_label || '—'}</span>
                  <span>Score</span>
                  <span>
                    {typeof row.improvement_score === 'number'
                      ? `${row.improvement_score}%`
                      : 'Score pending'}
                  </span>
                  {row.suggested_words ? (
                    <>
                      <span>Suggested words</span>
                      <span>~{row.suggested_words}</span>
                    </>
                  ) : null}
                  {modelId ? (
                    <>
                      <span>AI model</span>
                      <span>{modelId}</span>
                    </>
                  ) : null}
                </MetaRow>
                <Helper style={{ marginTop: theme.spacing[3] }}>
                  Disclosure of AI use is required by the UK Cabinet Office Procurement Policy
                  Note (PPN) on AI in bids. The status, model, and per-question scoring breakdown
                  below are the audit trail for this question.
                </Helper>
              </Section>

              {meta && (
                <Section>
                  <SectionTitle>
                    <BarChart3 size={14} /> Why this question {meta.status === 'selected' ? 'was selected' : meta.status === 'no_response' ? 'is awaiting a draft' : 'was not refined'}
                  </SectionTitle>
                  <MetaRow style={{ marginBottom: theme.spacing[3] }}>
                    <span>Selection status</span>
                    <span>
                      {selectionInfo ? (
                        <StatusPill $tone={selectionInfo.tone}>{selectionInfo.label}</StatusPill>
                      ) : (
                        meta.status
                      )}
                    </span>
                    <span>Final score</span>
                    <span>
                      {typeof meta.final_score === 'number'
                        ? `${meta.final_score.toFixed(1)} / ${100}`
                        : '—'}
                    </span>
                    <span>Threshold</span>
                    <span>{meta.threshold}</span>
                  </MetaRow>

                  {(typeof breakdown.type === 'number' ||
                    typeof breakdown.length === 'number' ||
                    typeof breakdown.criteria === 'number' ||
                    typeof breakdown.keywords === 'number') && (
                    <ScoreBars>
                      {(['type', 'length', 'criteria', 'keywords'] as const).map((k) => {
                        const v = (breakdown as any)[k] as number | undefined;
                        if (typeof v !== 'number') return null;
                        const w = weights[k];
                        const pct = Math.max(0, Math.min(100, (v / w) * 100));
                        return (
                          <ScoreBar key={k}>
                            <span className="label">
                              {k === 'type' && 'Question type'}
                              {k === 'length' && 'Response length'}
                              {k === 'criteria' && 'Criterion match'}
                              {k === 'keywords' && 'Keywords'}
                            </span>
                            <span className="track">
                              <span
                                className="fill"
                                style={{ width: `${pct}%`, display: 'block' }}
                              />
                            </span>
                            <span className="value">
                              {v.toFixed(1)} / {w}
                            </span>
                          </ScoreBar>
                        );
                      })}
                    </ScoreBars>
                  )}

                  {meta.reasons && meta.reasons.length > 0 && (
                    <ul
                      style={{
                        marginTop: theme.spacing[3],
                        paddingLeft: theme.spacing[5],
                        color: theme.colors.textPrimary,
                        fontSize: theme.typography.fontSize.sm,
                        lineHeight: theme.typography.lineHeight.relaxed,
                      }}
                    >
                      {meta.reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}

              {row.improvement_summary && (
                <Section>
                  <SectionTitle>
                    <Sparkles size={14} /> What changed in this {row.status === 'drafted' ? 'draft' : 'refinement'}
                  </SectionTitle>
                  <Helper style={{ color: theme.colors.textPrimary }}>
                    {row.improvement_summary}
                  </Helper>
                </Section>
              )}

              {/* Tender-bank similarity matches — populated by match_past_tenders.
                  Hidden when the bank is empty; otherwise shows top 3 past Q&A
                  passages with relevance score and outcome (won / lost) badge. */}
              {row.past_matches && row.past_matches.length > 0 && (
                <Section>
                  <SectionTitle>
                    <Database size={14} /> Similar past responses ({row.past_matches.length})
                  </SectionTitle>
                  <Helper style={{ marginBottom: theme.spacing[2] }}>
                    From your tender bank, ranked by semantic similarity to this question.
                  </Helper>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3] }}>
                    {row.past_matches.map((m, i) => (
                      <div
                        key={`${m.tender_id}-${i}`}
                        style={{
                          background: theme.colors.surface,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.borderRadius.md,
                          padding: theme.spacing[3],
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: theme.spacing[2],
                            marginBottom: theme.spacing[2],
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.semibold,
                                color: theme.colors.textPrimary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={m.tender_title}
                            >
                              {m.tender_title}
                            </div>
                            <div
                              style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.textMuted,
                                display: 'flex',
                                gap: theme.spacing[2],
                                marginTop: 2,
                              }}
                            >
                              {typeof m.relevance_score === 'number' && (
                                <span>
                                  Relevance{' '}
                                  <strong style={{ color: theme.colors.textPrimary }}>
                                    {(m.relevance_score * 100).toFixed(0)}%
                                  </strong>
                                </span>
                              )}
                              {m.outcome && (
                                <StatusPill
                                  $tone={
                                    m.outcome === 'won'
                                      ? 'success'
                                      : m.outcome === 'lost'
                                      ? 'error'
                                      : 'neutral'
                                  }
                                >
                                  {m.outcome}
                                </StatusPill>
                              )}
                              {typeof m.tender_score === 'number' && (
                                <span>
                                  Score <strong>{m.tender_score}</strong>
                                </span>
                              )}
                              {/* Phase 2 — Q&A match indicator. Q&A pairs
                                  are higher-precision than prose chunks
                                  (question-to-question matching), so we
                                  surface them with a primary-tone pill. */}
                              {m.match_type === 'qa' && (
                                <StatusPill $tone="info">Q&amp;A match</StatusPill>
                              )}
                              {typeof m.rerank_score === 'number' && m.rerank_score > 0 && (
                                <span>
                                  Rerank{' '}
                                  <strong style={{ color: theme.colors.textPrimary }}>
                                    {(m.rerank_score * 100).toFixed(0)}%
                                  </strong>
                                </span>
                              )}
                            </div>
                            {/* Phase 3 — boost-breakdown explanation. Shows
                                why this match ranked where it did. Hidden
                                when no contextual boosts fired (the line
                                would just say "outcome_won + qa_chunk"
                                redundantly with the existing pills). */}
                            {(() => {
                              if (!m.boost_breakdown) return null;
                              const ctxKeys = [
                                'framework_match',
                                'buyer_match',
                                'value_band_match',
                                'geo_match',
                                'criterion_match',
                                'industry_match',
                              ];
                              const fired = ctxKeys
                                .filter((k) => (m.boost_breakdown![k] || 0) > 0)
                                .map((k) => k.replace('_match', '').replace('_', ' '));
                              if (!fired.length) return null;
                              const totalBoost = m.boost_breakdown['_total_boost'];
                              return (
                                <div
                                  style={{
                                    marginTop: 4,
                                    fontSize: theme.typography.fontSize.xs,
                                    color: theme.colors.textMuted,
                                  }}
                                  title={JSON.stringify(m.boost_breakdown, null, 2)}
                                >
                                  Matched on:{' '}
                                  <strong style={{ color: theme.colors.success[700] }}>
                                    {fired.join(' · ')}
                                  </strong>
                                  {typeof totalBoost === 'number' && (
                                    <>
                                      {' '}
                                      <span style={{ color: theme.colors.textMuted }}>
                                        ({totalBoost >= 0 ? '+' : ''}
                                        {totalBoost.toFixed(3)})
                                      </span>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Phase 2 — when this is a Q&A pair, render the
                            past question prominently above the answer.
                            Lets the bidder see "they asked X, we answered
                            Y" at a glance, rather than reading prose and
                            inferring the question. */}
                        {m.match_type === 'qa' && m.past_question && (
                          <div
                            style={{
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.colors.textMuted,
                              marginBottom: theme.spacing[2],
                              padding: theme.spacing[2],
                              borderLeft: `3px solid ${theme.colors.primary[400]}`,
                              background: theme.colors.primary[50],
                              borderRadius: theme.borderRadius.sm,
                            }}
                          >
                            <div
                              style={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontWeight: theme.typography.fontWeight.semibold,
                                color: theme.colors.primary[700],
                                marginBottom: 2,
                              }}
                            >
                              Past question
                            </div>
                            <div
                              style={{
                                color: theme.colors.textPrimary,
                                fontSize: theme.typography.fontSize.sm,
                                lineHeight: 1.4,
                              }}
                            >
                              {m.past_question}
                            </div>
                          </div>
                        )}

                        {/* Phase 1 — Tender Bank enriched metadata strip.
                            Renders only the fields that are populated, so
                            older entries (pre-Phase 1) don't show empty
                            slots. Each chip is a low-noise grey unless the
                            value carries a strong signal (criterion match
                            ≥ 0.15 → primary tone). */}
                        {(m.buyer_organisation ||
                          m.contract_value_band ||
                          m.framework_reference ||
                          (m.geographic_scope && m.geographic_scope.length > 0) ||
                          m.criterion_match ||
                          m.section) && (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: theme.spacing[1],
                              marginBottom: theme.spacing[2],
                              fontSize: theme.typography.fontSize.xs,
                            }}
                          >
                            {m.buyer_organisation && (
                              <StatusPill $tone="neutral">{m.buyer_organisation}</StatusPill>
                            )}
                            {m.contract_value_band && (
                              <StatusPill $tone="neutral">{m.contract_value_band}</StatusPill>
                            )}
                            {m.framework_reference && (
                              <StatusPill $tone="info">{m.framework_reference}</StatusPill>
                            )}
                            {m.geographic_scope && m.geographic_scope.length > 0 && (
                              <StatusPill $tone="neutral">
                                {m.geographic_scope.slice(0, 2).join(' · ')}
                                {m.geographic_scope.length > 2 ? ` +${m.geographic_scope.length - 2}` : ''}
                              </StatusPill>
                            )}
                            {m.section && m.section !== 'Body' && (
                              <StatusPill $tone="neutral">§ {m.section}</StatusPill>
                            )}
                            {m.criterion_match && (
                              <StatusPill
                                $tone={
                                  typeof m.criterion_match_score === 'number' && m.criterion_match_score >= 0.15
                                    ? 'success'
                                    : 'info'
                                }
                              >
                                Criterion: {m.criterion_match}
                              </StatusPill>
                            )}
                          </div>
                        )}

                        <div
                          style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.colors.textPrimary,
                            lineHeight: theme.typography.lineHeight.relaxed,
                            background: theme.colors.backgroundSecondary,
                            padding: theme.spacing[2],
                            borderRadius: theme.borderRadius.sm,
                            maxHeight: 200,
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {m.chunk_text}
                        </div>

                        {/* Phase 6 — counterfactual learning capture.
                            Bidder rates the chunk; the calibrator
                            (nightly) uses these signals to update
                            per-tenant boost weights. */}
                        {companyId && (() => {
                          const chunkKey = `${m.tender_id}#${m.chunk_index ?? 0}`;
                          const submitted = feedbackActions[chunkKey];
                          return (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing[2],
                                marginTop: theme.spacing[2],
                                fontSize: theme.typography.fontSize.xs,
                              }}
                            >
                              <span style={{ color: theme.colors.textMuted }}>
                                Was this match useful?
                              </span>
                              <button
                                onClick={() => submitFeedback(m, 'accepted')}
                                disabled={!!submitted}
                                title="Useful — boost similar matches in future"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '2px 10px',
                                  border: `1px solid ${
                                    submitted === 'accepted'
                                      ? theme.colors.success[500]
                                      : theme.colors.border
                                  }`,
                                  background:
                                    submitted === 'accepted'
                                      ? theme.colors.success[100]
                                      : theme.colors.surface,
                                  color:
                                    submitted === 'accepted'
                                      ? theme.colors.success[700]
                                      : theme.colors.textSecondary,
                                  borderRadius: theme.borderRadius.full,
                                  cursor: submitted ? 'default' : 'pointer',
                                  opacity: submitted && submitted !== 'accepted' ? 0.4 : 1,
                                }}
                              >
                                <ThumbsUp size={11} />
                                Useful
                              </button>
                              <button
                                onClick={() => submitFeedback(m, 'rejected')}
                                disabled={!!submitted}
                                title="Not useful — demote similar matches in future"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '2px 10px',
                                  border: `1px solid ${
                                    submitted === 'rejected'
                                      ? theme.colors.error[500]
                                      : theme.colors.border
                                  }`,
                                  background:
                                    submitted === 'rejected'
                                      ? theme.colors.error[100]
                                      : theme.colors.surface,
                                  color:
                                    submitted === 'rejected'
                                      ? theme.colors.error[700]
                                      : theme.colors.textSecondary,
                                  borderRadius: theme.borderRadius.full,
                                  cursor: submitted ? 'default' : 'pointer',
                                  opacity: submitted && submitted !== 'rejected' ? 0.4 : 1,
                                }}
                              >
                                <ThumbsDown size={11} />
                                Not useful
                              </button>
                              {submitted && (
                                <span style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>
                                  Thanks — we'll learn from this.
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {row.source === 'drafted' && (() => {
                const coverage = row.kb_retrieval?.coverage || null;
                const sources = row.kb_sources || [];
                const retrieval = row.kb_retrieval || {};
                const assessment = (coverage?.assessment || '').toLowerCase();
                const tone: 'success' | 'info' | 'warning' | 'error' | 'neutral' =
                  assessment === 'excellent' || assessment === 'good'
                    ? 'success'
                    : assessment === 'fair'
                    ? 'info'
                    : assessment === 'poor'
                    ? 'warning'
                    : assessment === 'critical'
                    ? 'error'
                    : 'neutral';
                const confidencePct =
                  typeof coverage?.confidence_score === 'number'
                    ? Math.max(0, Math.min(100, coverage.confidence_score * 100))
                    : null;
                const avgRelevancePct =
                  typeof retrieval.avg_relevance === 'number'
                    ? Math.max(0, Math.min(100, retrieval.avg_relevance * 100))
                    : null;

                return (
                  <Section>
                    <SectionTitle>
                      <ShieldCheck size={14} /> KB coverage for this question
                    </SectionTitle>
                    <Helper style={{ marginBottom: theme.spacing[3] }}>
                      How well your company's knowledge base supports this question. Drives
                      whether the AI draft can be grounded or will read as generic.
                    </Helper>

                    {coverage ? (
                      <>
                        <MetaRow style={{ marginBottom: theme.spacing[3] }}>
                          <span>Assessment</span>
                          <span>
                            {coverage.assessment ? (
                              <StatusPill $tone={tone}>{coverage.assessment}</StatusPill>
                            ) : (
                              '—'
                            )}
                          </span>
                          {confidencePct !== null && (
                            <>
                              <span>Confidence</span>
                              <span>{confidencePct.toFixed(0)}%</span>
                            </>
                          )}
                          {typeof retrieval.source_count === 'number' && (
                            <>
                              <span>Sources retrieved</span>
                              <span>{retrieval.source_count}</span>
                            </>
                          )}
                          {avgRelevancePct !== null && (
                            <>
                              <span>Avg. relevance</span>
                              <span>{avgRelevancePct.toFixed(0)}%</span>
                            </>
                          )}
                          {retrieval.retrieval_method && (
                            <>
                              <span>Method</span>
                              <span>{retrieval.retrieval_method}</span>
                            </>
                          )}
                        </MetaRow>

                        {coverage.missing_categories && coverage.missing_categories.length > 0 && (
                          <div style={{ marginBottom: theme.spacing[3] }}>
                            <Helper style={{ marginBottom: theme.spacing[2] }}>
                              <strong>Gaps —</strong> categories the KB doesn't yet cover for
                              this question:
                            </Helper>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing[2] }}>
                              {coverage.missing_categories.map((cat, i) => (
                                <StatusPill key={`${cat}-${i}`} $tone="warning">
                                  {cat}
                                </StatusPill>
                              ))}
                            </div>
                          </div>
                        )}

                        {coverage.recommendations && coverage.recommendations.length > 0 && (
                          <div style={{ marginBottom: theme.spacing[3] }}>
                            <Helper style={{ marginBottom: theme.spacing[2] }}>
                              <strong>Recommendations —</strong> upload these to strengthen
                              the draft:
                            </Helper>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: theme.spacing[5],
                                color: theme.colors.textPrimary,
                                fontSize: theme.typography.fontSize.sm,
                                lineHeight: theme.typography.lineHeight.relaxed,
                              }}
                            >
                              {coverage.recommendations.map((rec, i) => (
                                <li key={i}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : retrieval.error ? (
                      <Helper style={{ color: theme.colors.error[600] }}>
                        Coverage assessment unavailable: {retrieval.error}
                      </Helper>
                    ) : retrieval.reason ? (
                      <Helper>{retrieval.reason}</Helper>
                    ) : (
                      <Helper>
                        Coverage data was not produced for this draft (older run, or the
                        retrieval step was skipped).
                      </Helper>
                    )}

                    {sources.length > 0 && (
                      <div style={{ marginTop: theme.spacing[3] }}>
                        <Helper style={{ marginBottom: theme.spacing[2] }}>
                          <strong>Documents cited ({sources.length}):</strong>
                        </Helper>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing[2],
                          }}
                        >
                          {sources.slice(0, 8).map((s, i) => (
                            <SourceItem key={`${s.document_id || i}`}>
                              <div className="title">{s.title || s.document_id || 'Untitled'}</div>
                              <div className="meta">
                                {s.category ? `${s.category} · ` : ''}
                                {typeof s.relevance_score === 'number'
                                  ? `relevance ${(s.relevance_score * 100).toFixed(0)}%`
                                  : ''}
                              </div>
                            </SourceItem>
                          ))}
                        </div>
                      </div>
                    )}
                  </Section>
                );
              })()}

              {/* For refined / drafted rows, render the full ResponseComparisonCard
                  with version history, diff view, word count panel, faithfulness
                  warning, accept/reject/regenerate, and AI Refine. */}
              {wide && adaptedRefined && (
                <Section>
                  <ResponseComparisonCard
                    refinedResponse={adaptedRefined}
                    index={0}
                    questionType={row.type}
                    criterionLabel={row.criterion_label}
                    onHumanRefine={onHumanRefine}
                    onAcceptRefinement={onAcceptRefinement}
                    /* Per-row coverage assessment is the authoritative
                       per-card signal — when the KB was empty at draft time
                       this row's tier is 'ungrounded' regardless of run
                       state. The card renders its bracketed-placeholder
                       warning based on this. */
                    isUngrounded={row.kb_retrieval?.coverage?.assessment === 'ungrounded'}
                  />
                </Section>
              )}

              {/* needs_draft / user_excluded etc — show the original (if any)
                  as plain markdown so the user can still see the source. */}
              {!wide && row.original_response && (
                <Section>
                  <SectionTitle>
                    <FileText size={14} /> Original bidder response
                  </SectionTitle>
                  <ResponseBox>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {row.original_response}
                    </ReactMarkdown>
                  </ResponseBox>
                </Section>
              )}

              {row.status === 'needs_draft' && (
                <Section>
                  <SectionTitle>
                    <AlertTriangle size={14} /> What "Awaiting draft" means
                  </SectionTitle>
                  <Helper>
                    This question was successfully extracted from the tender, but no
                    inline bidder response was found in the source document <em>and</em>{' '}
                    the auto-draft step has not yet produced one. Two ways to resolve it:
                    re-run the analysis (drafts may have been skipped if KB retrieval was
                    misconfigured), or upload supporting case studies / certifications /
                    team bios into your Knowledge Base so the AI has grounded material to
                    draft from.
                  </Helper>
                </Section>
              )}
              </>
              )}
            </Body>
          </>
        )}
      </Panel>
    </>
  );
};

export default QuestionDetailDrawer;
